import { useState } from "react";
import { HelpCircle, Swords, Shield, Target, Scale, Zap, Heart, Crosshair, Wind } from "lucide-react";
import Modal from "@/components/Modal";

const STYLES = [
  { name: "Aggressive", icon: Swords, color: "text-red-400",
    text: "All-out offense. Overwhelms measured, balanced opponents — but a patient defensive fighter can turn that recklessness against you." },
  { name: "Balanced", icon: Scale, color: "text-amber-400",
    text: "Adaptable and safe. Holds a slight edge over a turtling defender and is never hard-countered — yet a committed aggressor can seize the initiative." },
  { name: "Defensive", icon: Shield, color: "text-sky-400",
    text: "Patient and durable. Punishes reckless attackers — but a balanced opponent can pick apart the guard." },
];

const CLASSES = [
  { name: "Dimachaerus", role: "The Dual-Wielder", icon: Swords, color: "text-red-400",
    text: "Ferocious and fast. Strikes hard and moves quickly, but leaves openings and can't absorb much punishment." },
  { name: "Retiarius", role: "The Net-Fighter", icon: Target, color: "text-sky-400",
    text: "The all-rounder. No glaring weakness — wins through control, disruption, and tempo." },
  { name: "Provocator", role: "The Heavy", icon: Shield, color: "text-amber-400",
    text: "The wall. Endures punishment and outlasts foes in long fights, but slow on their feet and softer-hitting." },
];

const SKILLS = [
  { name: "Strength", icon: Swords, color: "text-red-400", text: "Your raw hitting power — the primary source of attack damage, with a little added toughness." },
  { name: "Speed", icon: Wind, color: "text-sky-400", text: "Determines initiative and who keeps the tempo in a fight. Dimachaerus need high speed." },
  { name: "Defense", icon: Shield, color: "text-zinc-300", text: "How well you shrug off incoming blows and reduce the damage you take." },
  { name: "Stamina", icon: Heart, color: "text-orange-400", text: "Your endurance — the main source of your health pool and staying power." },
  { name: "Technique", icon: Zap, color: "text-purple-400", text: "The versatile skill — sharpens both your offense and your defense at once." },
  { name: "Precision", icon: Crosshair, color: "text-emerald-400", text: "Your knack for landing devastating critical hits, plus a touch of extra damage." },
];

const CLASS_SKILL_NEEDS = [
  {
    name: "Provocator",
    icon: Shield,
    color: "text-amber-400",
    text: "A durable, defensive fighter built for long battles.\nBest stats: Defense, Stamina, Strength\nBest stance: Defensive",
  },
  {
    name: "Dimachaerus",
    icon: Swords,
    color: "text-red-400",
    text: "A fast, aggressive attacker who wins through pressure and damage.\nBest stats: Speed, Strength, Precision\nBest stance: Aggressive",
  },
  {
    name: "Retiarius",
    icon: Target,
    color: "text-sky-400",
    text: "A technical fighter who relies on control, timing, and precision.\nBest stats: Technique, Precision, Speed\nBest stance: Balanced",
  },
];

function GuideButton({ label, testid, onClick }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-semibold text-amber-400 hover:text-amber-300 transition-colors">
      <HelpCircle size={13} /> {label}
    </button>
  );
}

function Row({ item, sub }) {
  const I = item.icon;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-amber-900/10 last:border-0">
      <I size={16} className={`${item.color} mt-0.5 shrink-0`} />
      <div>
        <div className="text-sm text-zinc-100 font-medium">
          {item.name}{sub && <span className="text-zinc-500 font-normal"> · {item[sub]}</span>}
        </div>
        <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed whitespace-pre-line">{item.text}</p>
      </div>
    </div>
  );
}

export function CombatGuideButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <GuideButton label="Combat Guide" testid="combat-guide-btn" onClick={() => setOpen(true)} />
      <Modal open={open} onClose={() => setOpen(false)} title="Combat Guide" wide>
        <div className="space-y-6">
          <section>
            <h4 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-1">Fighting Styles</h4>
            <p className="text-[11px] text-zinc-500 mb-2">
              Styles form a cycle — read your opponent and the right stance tilts the whole fight.
              <span className="text-zinc-400"> Aggressive ▸ Balanced ▸ Defensive ▸ Aggressive.</span>
            </p>
            {STYLES.map((s) => <Row key={s.name} item={s} />)}
          </section>
          <section>
            <h4 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-1">Classes</h4>
            <p className="text-[11px] text-zinc-500 mb-2">No class hard-counters another — each is a different set of strengths. Learn the matchups in the arena.</p>
            {CLASSES.map((c) => <Row key={c.name} item={c} sub="role" />)}
          </section>
        </div>
      </Modal>
    </>
  );
}

export function SkillsGuideButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <GuideButton label="What do skills do?" testid="skills-guide-btn" onClick={() => setOpen(true)} />
      <Modal open={open} onClose={() => setOpen(false)} title="Skills Guide">
        <p className="text-[11px] text-zinc-500 mb-2">How each skill shapes your gladiator in the arena. Exact strengths are yours to discover through training and combat.</p>
        {SKILLS.map((s) => <Row key={s.name} item={s} />)}
      </Modal>
    </>
  );
}

export function ClassSkillsGuideButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <GuideButton label="What skills do classes need?" testid="class-skills-guide-btn" onClick={() => setOpen(true)} />
      <Modal open={open} onClose={() => setOpen(false)} title="What skills do classes need?">
        {CLASS_SKILL_NEEDS.map((item) => <Row key={item.name} item={item} />)}
      </Modal>
    </>
  );
}
